import { useEffect, useState } from 'react'
import { AlertCircle, Loader2, Server } from 'lucide-react'

type ServerStatus = 'checking' | 'ready' | 'warming-up' | 'offline'

interface ServerStatusIndicatorProps {
  onStatusChange?: (status: ServerStatus) => void
  autoCheck?: boolean
  checkInterval?: number
}

export function ServerStatusIndicator({
  onStatusChange,
  autoCheck = true,
  checkInterval = 30000, // Check every 30 seconds
}: ServerStatusIndicatorProps) {
  const [status, setStatus] = useState<ServerStatus>('checking')
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    if (!autoCheck) return

    const checkServer = async () => {
      try {
        setStatus('checking')
        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), 5000) // 5 second timeout

        const response = await fetch('/api/health', {
          method: 'HEAD',
          signal: controller.signal,
        })

        clearTimeout(timeoutId)

        if (response.ok) {
          setStatus('ready')
          setIsVisible(false)
        } else {
          setStatus('warming-up')
          setIsVisible(true)
        }
      } catch {
        setStatus('offline')
        setIsVisible(true)
      }
    }

    checkServer()
    const interval = setInterval(checkServer, checkInterval)

    return () => clearInterval(interval)
  }, [autoCheck, checkInterval])

  useEffect(() => {
    onStatusChange?.(status)
  }, [status, onStatusChange])

  if (!isVisible) return null

  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-sm">
      {status === 'warming-up' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 shadow-lg">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">
              <Loader2 size={20} className="text-blue-600 animate-spin" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-blue-900 text-sm">Server is warming up</h3>
              <p className="text-blue-700 text-xs mt-1">
                The server is currently starting up. This usually takes a few moments. Please wait…
              </p>
            </div>
          </div>
        </div>
      )}

      {status === 'offline' && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 shadow-lg">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">
              <AlertCircle size={20} className="text-red-600" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-red-900 text-sm">Server unavailable</h3>
              <p className="text-red-700 text-xs mt-1">
                The server is currently unavailable. Please try again in a few moments.
              </p>
            </div>
          </div>
        </div>
      )}

      {status === 'checking' && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 shadow-lg">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">
              <Server size={20} className="text-gray-600 animate-pulse" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-gray-900 text-sm">Checking server status</h3>
              <p className="text-gray-600 text-xs mt-1">
                Please wait while we check the server connection…
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ServerStatusIndicator
